/**
 *  * src/utils/undoRedo/commands/BellButtonCommand.js
 * BellButtonCommand.js
 * Bell Button + Wire ekleme/silme komutları
 *
 * Bell Button eklenirken:
 * 1. Bell Button symbol sahneye eklenir
 * 2. Wire (polyline) sahneye eklenir (bell wire'dan button'a)
 */

import BaseCommand from './BaseCommand';
import { addWire, deleteWire } from '../../../redux/wireSlice';
import * as THREE from 'three';
import { createBellButton } from '../../../factory/symbols/createBellSymbols';
import sceneObjectCache from '../SceneObjectCache';
import { batch } from 'react-redux';

class BellButtonCommand extends BaseCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} sceneData - Three.js scene verisi (button position, wire vertices, etc.)
   */
  constructor(action, sceneData) {
    super(action === 'ADD' ? 'ADD_BELL_BUTTON' : 'DELETE_BELL_BUTTON', {
      before: action === 'DELETE' ? { sceneData } : null,
      after: action === 'ADD' ? { sceneData } : null
    }, sceneData);

    this.action = action; // 'ADD' veya 'DELETE'
  }

  /**
   * Bell Button + Wire ekleme/silme işlemini gerçekleştir
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla (performans için)
   */
  execute(scene, dispatch, skipRedux = false) {
    if (this.action === 'ADD') {
      this._addBellButton(scene, dispatch, skipRedux);
    } else if (this.action === 'DELETE') {
      this._deleteBellButton(scene, dispatch, skipRedux);
    }
  }

  /**
   * Bell Button + Wire ekleme/silme işlemini geri al
   */
  undo(scene, dispatch) {
    if (this.action === 'ADD') {
      // ADD işlemini geri al = DELETE yap
      this._deleteBellButton(scene, dispatch);
    } else if (this.action === 'DELETE') {
      // DELETE işlemini geri al = ADD yap
      this._addBellButton(scene, dispatch);
    }
  }

  /**
   * Sahneye Bell Button + Wire ekle
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla
   */
  _addBellButton(scene, dispatch, skipRedux = false) {
    // Wire rengi sceneData'dan al
    const wireColor = this.sceneData.color || 0x0045ff;

    // 1. Three.js sahnesine bell button ekle
    const buttonPosition = new THREE.Vector3(
      this.sceneData.buttonPosition[0],
      this.sceneData.buttonPosition[1],
      this.sceneData.buttonPosition[2]
    );

    const bellButton = createBellButton(
      scene,
      buttonPosition,
      wireColor,
      false // isPreview
    );

    // ✅ Bell button açısını ayarla (wire yönüne göre)
    const buttonAngle = this.sceneData.buttonAngle || 0;
    bellButton.rotation.z = buttonAngle;

    // ✅ Bell button userData'sını güncelle
    bellButton.userData = {
      ...bellButton.userData,
      id: this.sceneData.buttonId,
      branchID: this.sceneData.branchID,
      wireId: this.sceneData.wireId,
      type: 'BellButton',
      isElectrical: true
    };

    scene.add(bellButton);

    // ✅ Cache'e ekle
    sceneObjectCache.addFixture(this.sceneData.buttonId, bellButton);

    // 1.5. Bell wire ile bellButton wire kesişim noktasına yuvarlak buat ekle
    if (this.sceneData.vertices && this.sceneData.vertices.length > 0) {
      const connectionPoint = new THREE.Vector3(
        this.sceneData.vertices[0].x,
        this.sceneData.vertices[0].y,
        this.sceneData.vertices[0].z || 0
      );

      // ✅ Yuvarlak buat (connection marker) oluştur
      const circleGeometry = new THREE.CircleGeometry(3, 16); // 3 birim yarıçap, 16 segment
      const circleMaterial = new THREE.MeshBasicMaterial({
        color: wireColor,
        side: THREE.DoubleSide
      });
      const connectionMarker = new THREE.Mesh(circleGeometry, circleMaterial);
      connectionMarker.position.copy(connectionPoint);

      // ✅ userData ekle
      connectionMarker.userData = {
        id: `${this.sceneData.buttonId}_connection`,
        type: 'bellButtonConnection',
        parentId: this.sceneData.buttonId,
        branchID: this.sceneData.branchID
      };

      scene.add(connectionMarker);

      // ✅ Connection marker'ı cache'e ekle (bellButton ile birlikte silinmesi için)
      sceneObjectCache.addFixture(`${this.sceneData.buttonId}_connection`, connectionMarker);
    }

    // 2. Three.js sahnesine wire (polyline) ekle
    if (this.sceneData.vertices && this.sceneData.vertices.length > 1) {
      const wireVertices = this.sceneData.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z));

      const wireGeometry = new THREE.BufferGeometry().setFromPoints(wireVertices);
      const wireMaterial = new THREE.LineBasicMaterial({ color: wireColor });
      const wireLine = new THREE.Line(wireGeometry, wireMaterial);

      wireLine.userData = {
        id: this.sceneData.wireId,
        type: 'bellButtonWire',
        wireId: this.sceneData.wireId,
        branchID: this.sceneData.branchID,
        isSelectable: true
      };

      scene.add(wireLine);

      // ✅ Wire'ı cache'e ekle
      sceneObjectCache.addFeeder(this.sceneData.wireId, wireLine);

      // ✅ Redux wireSlice'a kaydet (skipRedux=false ise)
      if (!skipRedux) {
        dispatch(addWire({
          id: this.sceneData.wireId,
          sceneUUID: wireLine.uuid,
          type: 'polyline',
          vertices: this.sceneData.vertices,
          layer: 'bellButton',
          color: wireColor,
          userData: {
            selectable: true,
            parent: 'bellButton',
            branchID: this.sceneData.branchID
          }
        }));
      }
    }
  }

  /**
   * Sahneden Bell Button + Wire sil
   */
  _deleteBellButton(scene, dispatch) {
    // 1. Redux'tan wire sil (batch ile tek re-render)
    batch(() => {
      dispatch(deleteWire(this.sceneData.wireId));
    });

    // 2. Three.js sahnesinden bell button sil - ✅ Cache'den al
    const buttonToRemove = sceneObjectCache.getFixture(this.sceneData.buttonId);

    if (buttonToRemove) {
      buttonToRemove.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });

      if (buttonToRemove.parent) {
        buttonToRemove.parent.remove(buttonToRemove);
      } else {
        scene.remove(buttonToRemove);
      }

      // ✅ Cache'den sil
      sceneObjectCache.removeFixture(this.sceneData.buttonId);
    }

    // 2.5. Connection marker'ı sil - ✅ Cache'den al
    const connectionMarkerToRemove = sceneObjectCache.getFixture(`${this.sceneData.buttonId}_connection`);

    if (connectionMarkerToRemove) {
      if (connectionMarkerToRemove.geometry) connectionMarkerToRemove.geometry.dispose();
      if (connectionMarkerToRemove.material) connectionMarkerToRemove.material.dispose();
      scene.remove(connectionMarkerToRemove);

      // ✅ Cache'den sil
      sceneObjectCache.removeFixture(`${this.sceneData.buttonId}_connection`);
    }

    // 3. Three.js sahnesinden wire sil - ✅ Cache'den al
    const wireToRemove = sceneObjectCache.getFeeder(this.sceneData.wireId);

    if (wireToRemove) {
      if (wireToRemove.geometry) wireToRemove.geometry.dispose();
      if (wireToRemove.material) wireToRemove.material.dispose();
      scene.remove(wireToRemove);

      // ✅ Cache'den sil
      sceneObjectCache.removeFeeder(this.sceneData.wireId);
    }
  }

  /**
   * JSON'dan BellButtonCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_BELL_BUTTON' ? 'ADD' : 'DELETE';
    return new BellButtonCommand(action, json.sceneData);
  }
}

export default BellButtonCommand;
